/**
 * Club Service
 * 
 * API service layer for club-related operations with enhanced error handling.
 * 
 * **Migration status**: `getClubById` is on the gold `apiClient` (envelope).
 * `checkClubStatus` + `getClubPaymentSettings` still hit raw endpoints, so they
 * stay on `deprecatedApiClient` until those are converted.
 *
 * Error Handling:
 * - Network errors are automatically detected and enriched with context
 * - Server errors (5xx) are tagged appropriately
 * - All errors include user-friendly messages
 *
 * @see /apps/api/src/api/public/v1/controllers/clubControllers/
 */

import { apiClient } from '~/lib/apiClient';
import { legacyApiClient as deprecatedApiClient } from '~/lib/apiClient/legacyApiClient';
import { ApiError } from '~/lib/apiClient/errors';
import { ERROR_CODES } from '~/lib/apiClient/constants';
import type { ClubWithTenant, ClubStatus, ClubPaymentSettings } from '../types';

/**
 * Enhanced error wrapper for club service operations
 * 
 * Adds operation context to errors for better debugging and user messaging.
 */
function wrapClubError(error: unknown, operation: string, clubId?: number): never {
  if (error instanceof ApiError) {
    // Re-throw with additional context
    throw new ApiError(
      error.message,
      error.code,
      {
        ...error,
        details: {
          operation,
          clubId,
          originalError: error.details,
        }
      }
    );
  }
  
  // Unknown error type
  throw new ApiError(
    error instanceof Error ? error.message : 'Unknown error',
    ERROR_CODES.INTERNAL_ERROR,
    {
      userMessage: `Failed to ${operation}. Please try again.`,
      details: { operation, clubId }
    }
  );
}

/**
 * Validate club data has required fields for booking functionality
 * 
 * Throws descriptive errors if required fields are missing.
 * This centralizes validation so components don't need to check.
 * 
 * **IMPORTANT**: Distinguishes between:
 * - Empty/null data (API fetch failure) → Network error
 * - Valid data with missing fields (config issue) → Validation error
 * 
 * @param club - Club data to validate
 * @throws {ApiError} if validation fails
 */
function validateClubData(club: ClubWithTenant): void {
  // Check if we got completely empty/null data (indicates API failure, not config issue)
  if (!club || typeof club !== 'object' || Object.keys(club).length === 0) {
    throw new ApiError(
      'Failed to fetch club data',
      ERROR_CODES.NETWORK_ERROR,
      {
        status: 0,
        userMessage: 'Unable to load club information. Please check your connection and try again.',
        details: { 
          reason: 'empty_response',
          dataReceived: club 
        }
      }
    );
  }
  
  // Check if we got partial data without an ID (indicates malformed response)
  if (!club.id || !club.name) {
    throw new ApiError(
      'Received invalid club data',
      ERROR_CODES.NETWORK_ERROR,
      {
        status: 0,
        userMessage: 'Unable to load club information. Please try again.',
        details: { 
          reason: 'malformed_response',
          hasId: !!club.id,
          hasName: !!club.name
        }
      }
    );
  }
  
  // Pricing is deliberately NOT validated here. This used to assert, per mode,
  // that the club's pricing columns were complete — a hardcoded `['setPrice',
  // 'scalingPrice']` allowlist plus a per-mode completeness check — because the
  // browser priced bookings itself and needed the inputs.
  //
  // It no longer prices anything: `POST /bookings/validate` and `POST
  // /bookings/quote` do, and they are the only readers that can see a per-bay
  // premium or a new pricing mode. Keeping the check would mean a club on a mode
  // this build predates fails the allowlist and every booking page 422s with
  // "not properly configured" — a client refusing a club the server can price
  // perfectly well. A genuinely misconfigured club now surfaces where the
  // failure actually is: the quote returns no price and its own message.
  //
  // Hours and identity stay validated below — those the client does consume.

  if (!club.publicHours) {
    throw new ApiError(
      'Club is missing operating hours',
      ERROR_CODES.VALIDATION_ERROR,
      {
        status: 422,
        userMessage: 'This club is not properly configured for bookings. Please contact support.',
        details: { 
          missingField: 'publicHours',
          clubId: club.id,
          clubName: club.name 
        }
      }
    );
  }
}

/**
 * Fetch club by ID with tenant information
 * 
 * Automatically uses tenantHandle from context (via apiClient).
 * Enhanced error handling provides context about the operation.
 * 
 * **Data Validation**: identity and operating hours only — the two things this
 * app consumes directly. Pricing completeness is the server's to judge; see the
 * note in `validateClubData`.
 * 
 * @param clubId - Numeric club ID
 * @returns Club data with embedded tenant information
 * @throws {ApiError} with operation context if fetch fails or data is invalid
 * 
 * @example
 * ```typescript
 * try {
 *   const club = await getClubById(2);
 *   console.log(club.name); // "Driving Range Collective"
 *   // Prices come from the booking quote, never from these columns.
 * } catch (error) {
 *   if (isNetworkError(error)) {
 *     // Handle network error
 *   }
 * }
 * ```
 */
export async function getClubById(clubId: number): Promise<ClubWithTenant> {
  try {
    const result = await apiClient.get<ClubWithTenant>(`/clubs/${clubId}`, { skipAuth: true });
    if (!result.success) {
      throw new Error(result.message || 'Failed to fetch club data');
    }

    // Validate required fields for booking functionality
    // This ensures components never receive invalid data
    validateClubData(result.payload);

    return result.payload;
  } catch (error) {
    wrapClubError(error, 'fetch club data', clubId);
  }
}

/**
 * Check if club is currently open based on publicHours and scheduleExceptions
 * 
 * @param clubId - Numeric club ID
 * @returns Status object with isOpen flag and current time
 * @throws {ApiError} with operation context if fetch fails
 * 
 * @example
 * ```typescript
 * const status = await checkClubStatus(2);
 * if (status.isOpen) {
 *   console.log('Club is open!');
 * }
 * ```
 */
export async function checkClubStatus(clubId: number): Promise<ClubStatus> {
  try {
    const status = await deprecatedApiClient.get<ClubStatus>(`/clubs/${clubId}/status`);
    return status;
  } catch (error) {
    wrapClubError(error, 'check club status', clubId);
  }
}

/**
 * Get club payment settings (Stripe configuration)
 * 
 * @param clubId - Numeric club ID
 * @returns Payment settings including Stripe keys
 * @throws {ApiError} with operation context if fetch fails
 * 
 * @example
 * ```typescript
 * const settings = await getClubPaymentSettings(2);
 * console.log(settings.stripePublishableKey);
 * ```
 */
export async function getClubPaymentSettings(clubId: number): Promise<ClubPaymentSettings> {
  try {
    const settings = await deprecatedApiClient.get<ClubPaymentSettings>(`/clubs/${clubId}/payment-settings`);
    return settings;
  } catch (error) {
    wrapClubError(error, 'fetch payment settings', clubId);
  }
}
